import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { X, Download, Smartphone } from 'lucide-react';

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if user has dismissed before
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 7 * 24 * 60 * 60 * 1000) {
      // Don't show for 7 days after dismissal
      return;
    }

    // Listen for beforeinstallprompt event (Android/Desktop Chrome)
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after a short delay
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // For iOS, show custom prompt after delay if not standalone
    if (iOS && !standalone) {
      setTimeout(() => setShowPrompt(true), 5000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    }
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  // Don't render if already installed or no prompt available
  if (isStandalone || (!deferredPrompt && !isIOS)) {
    return null;
  }

  return (
    <AnimatePresence>
      {showPrompt && (
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50"
        >
          <div className="bg-gray-900 border-2 border-amber-600 rounded-lg shadow-[0_0_30px_rgba(251,191,36,0.3)] p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-amber-900/30 rounded-lg border border-amber-700">
                <Smartphone className="w-6 h-6 text-amber-500" />
              </div>
              <div className="flex-1">
                <h3 className="font-headline text-amber-500 tracking-wider text-sm mb-1">
                  INSTALL DEADLINE
                </h3>
                <p className="text-xs text-gray-400 font-typewriter mb-3">
                  {isIOS 
                    ? 'Tap the Share button, then "Add to Home Screen" for push notifications & quick access'
                    : 'Install for push notifications on mobile & quick access from your home screen'
                  }
                </p>
                <div className="flex gap-2">
                  {!isIOS && (
                    <Button
                      onClick={handleInstall}
                      size="sm"
                      className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider text-xs"
                    >
                      <Download className="w-3 h-3 mr-1" />
                      INSTALL
                    </Button>
                  )}
                  <Button
                    onClick={handleDismiss}
                    size="sm"
                    variant="ghost"
                    className="text-gray-400 hover:text-gray-300 font-typewriter text-xs"
                  >
                    {isIOS ? 'GOT IT' : 'NOT NOW'}
                  </Button>
                </div>
              </div>
              <button
                onClick={handleDismiss}
                className="text-gray-500 hover:text-gray-400 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InstallPrompt;
